import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { ConfirmDialog } from "../../components/common/confirmDialog";
import { CustomButton } from "../../components/common/customButton";
import { DynamicForm } from "../../components/common/DynamicForm";
import { ErrorDialog } from "../../components/common/errorDialog";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import {
  renderMultiLineTextInput,
  renderSubTitle,
} from "../../components/renderComponents";
import { dealFieldConfig } from "../../config/dealFieldConfig";
import { useCurrentUser } from "../../context/CurrentUserContext";
import { useDealProvider } from "../../context/DealContext";
import { useGlobalProvider } from "../../context/GlobalContext";
import { useReferenceLists } from "../../context/GlobalGetLists";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { apiGet } from "../../services/genesisApi/httpsMethods";
import ExtractedAssetsTable from "../assets/ExtractedAssetsTable";
import ExtractedContactsTable from "../contacts/ExtractedContactsTable";
import ExtractedTenantsTable from "../tenant/ExtractedTenantTable";
import type { KillReason, PartialDeal } from "../../types/deal";
import { KillReasonDialog } from "../../components/deal/KillReasonDialog";
import { hasKillReason, isKilledStatus } from "../../utils/dealStatusHelpers";
import { dealHasNda } from "../../types/nda";
import type { ExtractionResult } from "../../types/extraction";
import {
  getRequiredFields,
  getStatusName,
  isDealFieldFilled,
  type ValidationError,
} from "../../utils/dealValidation";
import {
  assetsFromExtraction,
  contactsFromExtraction,
  getInitialColumnOverrides,
  getInitialTenantRowRange,
  mergeExtractionDeal,
  mergeIncomingAssets,
  mergeIncomingContacts,
  mergeIncomingTenants,
  replaceExtractionTenants,
  tenantsFromExtraction,
} from "./applyExtractionToCreateForm";
import {
  dealConfigRowToPartialDeal,
  emptyDeal,
} from "./createDealUpdateHelpers";
import DealAiExtraction from "./DealAiExtraction";
import DealExcelUpload from "./DealExcelUpload";
import DealExtractionReview from "./DealExtractionReview";
import {
  fetchContactsLinkedToDeal,
  fetchDealAssets,
  fetchDealTenants,
  withDealBorrowers,
} from "./loadExistingDeal";
import { saveCreateDeal } from "./saveCreateDeal";
import { saveUpdateDeal } from "./saveUpdateDeal";
import {
  hasDealFieldChanges,
  hasRelatedRecordChanges,
} from "./hasEditDealChanges";
import {
  toExtractionNdaDraftForm,
  type ExtractionNdaDraft,
} from "../../utils/extractionNda";

export interface CreateDealEmbeddedToolbarState {
  submitLabel: string;
  isSubmitDisabled: boolean;
  isLoading: boolean;
  showDelete: boolean;
  onSubmit: () => void;
  onDelete: () => void;
}

interface CreateDealUpdateProps {
  deal?: PartialDeal;
  deleteOption?: boolean;
  extractionResult?: ExtractionResult;
  embedded?: boolean;
  onEmbeddedToolbarChange?: (
    state: CreateDealEmbeddedToolbarState | null,
  ) => void;
}

/**
 * Create / update deal page.
 * Deal fields + commentary + AI extraction + Excel upload + tables + save.
 * Can also be embedded on Extraction Detail.
 */
const CreateDealUpdate = ({
  deal: initialDeal,
  deleteOption = false,
  extractionResult: extractionResultProp,
  embedded = false,
  onEmbeddedToolbarChange,
}: CreateDealUpdateProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canEdit } = useCurrentUser();
  const { user } = useGlobalProvider();
  const { errorDialog, showError, showErrorFromResponse, hideError } =
    useErrorDialog();

  const {
    sponsors,
    sources,
    countries,
    sectors,
    status,
    users,
    loanTypes,
    riskClasses,
    securityTypes,
    transactionTypes,
    currencies,
    killReasons,
    capitalSources,
    contacts: referenceContacts,
  } = useReferenceLists();

  const {
    assets,
    tenants,
    contacts,
    setAssets,
    setTenants,
    setContacts,
    resetDeal,
    tenantAssetNameFieldHasError,
    assetIdsNeedingSave,
    clearAssetsNeedingSave,
  } = useDealProvider();

  const [deal, setDeal] = useState<PartialDeal>(initialDeal ?? emptyDeal);
  const dealRef = useRef(deal);
  dealRef.current = deal;
  const assetsRef = useRef(assets);
  assetsRef.current = assets;
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );
  const [isSaving, setIsSaving] = useState(false);
  // Status picked as Killed, applied once a kill reason is chosen.
  const [pendingKillStatus, setPendingKillStatus] = useState<unknown>(null);
  const [extractionFileName, setExtractionFileName] = useState<string | null>(
    null,
  );
  const [liveExtraction, setLiveExtraction] = useState<ExtractionResult | null>(
    null,
  );
  const [columnOverrides, setColumnOverrides] = useState<Record<string, string>>(
    {},
  );
  const [tenantRowRange, setTenantRowRange] = useState({ start: 10, end: 80 });
  const [extractionNdaDraft, setExtractionNdaDraft] =
    useState<ExtractionNdaDraft | null>(null);
  const [acceptExtractionNda, setAcceptExtractionNda] = useState(false);
  const lastAppliedCapitalSourceId = useRef<number | null>(null);
  const prevEditCapitalSourceId = useRef<number | undefined>(undefined);
  const hasStartedExtractionHydrate = useRef(false);
  // Ids of tenant rows that came from the current extraction preview
  const [extractionTenantIds, setExtractionTenantIds] = useState<
    ReadonlySet<unknown>
  >(() => new Set());
  const [originalAssets, setOriginalAssets] = useState<any[]>([]);
  const [originalTenants, setOriginalTenants] = useState<any[]>([]);
  const [originalContacts, setOriginalContacts] = useState<any[]>([]);
  const [originalDeal, setOriginalDeal] = useState<PartialDeal | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Deal saved but follow-up steps failed: the form must not be resubmitted
  // (it would create the deal / assets again), so leave for the deal page
  // once the user has read the error.
  const [partiallySavedDealId, setPartiallySavedDealId] = useState<
    number | null
  >(null);

  const isEdit = typeof initialDeal?.id === "number";
  const editDealId = isEdit ? (initialDeal.id as number) : null;
  const [existingDealReady, setExistingDealReady] = useState(!isEdit);

  const locationExtraction = (
    location.state as { extractionResult?: ExtractionResult } | null
  )?.extractionResult;
  const extractionResult = extractionResultProp ?? locationExtraction ?? null;
  const [extractionHydrated, setExtractionHydrated] = useState(
    !extractionResult,
  );

  // Start from a clean deal context unless we are hydrating an extraction or editing
  useEffect(() => {
    if (extractionResult || isEdit) return;
    resetDeal();
    setAssets([]);
    setTenants([]);
    setContacts([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the existing deal (and snapshots used later for update diffs)
  useEffect(() => {
    if (!isEdit || !initialDeal || editDealId == null) return;

    let cancelled = false;

    const load = async () => {
      prevEditCapitalSourceId.current = initialDeal.capital_source?.id ?? 1;
      lastAppliedCapitalSourceId.current = prevEditCapitalSourceId.current;

      const dealWithBorrowers = await withDealBorrowers(initialDeal);
      if (cancelled) return;
      setDeal(dealWithBorrowers);
      // Clone so later in-place edits do not mutate the snapshot (DealAccordionsTable pattern)
      setOriginalDeal(structuredClone(dealWithBorrowers));

      const [loadedAssets, loadedTenants, linkedContacts] = await Promise.all([
        fetchDealAssets(editDealId),
        fetchDealTenants(editDealId),
        fetchContactsLinkedToDeal(editDealId),
      ]);
      if (cancelled) return;

      setOriginalAssets(structuredClone(loadedAssets));
      setOriginalTenants(structuredClone(loadedTenants));
      setOriginalContacts(structuredClone(linkedContacts));
      setAssets(loadedAssets);
      setTenants(loadedTenants);
      setContacts(linkedContacts as any);

      setExistingDealReady(true);
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDealId]);

  // Load extracted deal / assets / tenants / contacts into the form once
  useEffect(() => {
    if (!extractionResult || hasStartedExtractionHydrate.current) return;
    if (isEdit && !existingDealReady) return;
    hasStartedExtractionHydrate.current = true;

    let cancelled = false;

    const hydrate = async () => {
      setExtractionFileName(extractionResult.fileName || null);

      const extractionDeal = (extractionResult.deal || {}) as PartialDeal;
      const capitalId =
        extractionDeal.capital_source?.id ??
        (isEdit ? deal.capital_source?.id : emptyDeal.capital_source?.id) ??
        1;

      let defaults: PartialDeal = {};
      if (!isEdit) {
        try {
          const response = await apiGet(
            `default-deal-config?capital_source_id=${capitalId}`,
          );
          if (response?.success && response.data?.[0]) {
            defaults = dealConfigRowToPartialDeal(
              response.data[0] as Record<string, unknown>,
            );
          }
        } catch {
          // Extraction values still apply without defaults
        }
      }

      if (cancelled) return;

      const baseDeal = isEdit ? dealRef.current : { ...emptyDeal, ...defaults };
      const mergedDeal = mergeExtractionDeal(baseDeal, extractionResult);
      lastAppliedCapitalSourceId.current =
        mergedDeal.capital_source?.id ?? capitalId;
      if (isEdit) {
        prevEditCapitalSourceId.current =
          mergedDeal.capital_source?.id ?? capitalId;
      }
      setDeal(mergedDeal);

      const nextAssets = assetsFromExtraction(extractionResult, editDealId);
      const nextContacts = contactsFromExtraction(extractionResult);
      const assetsForTenants = isEdit
        ? mergeIncomingAssets(assetsRef.current as any[], nextAssets)
        : nextAssets;
      const nextTenants = tenantsFromExtraction(
        extractionResult,
        assetsForTenants,
        mergedDeal,
      );
      setExtractionTenantIds(new Set(nextTenants.map((tenant) => tenant?.id)));

      if (isEdit) {
        setAssets((prev) => mergeIncomingAssets(prev as any[], nextAssets));
        setContacts((prev) =>
          mergeIncomingContacts(prev as any[], nextContacts),
        );
        setTenants((prev) => mergeIncomingTenants(prev as any[], nextTenants));
      } else {
        setAssets(nextAssets as any);
        setContacts(nextContacts as any);
        setTenants(nextTenants as any);
      }

      setLiveExtraction(extractionResult);
      setColumnOverrides(getInitialColumnOverrides(extractionResult));
      setTenantRowRange(getInitialTenantRowRange(extractionResult));
      const draftNda = toExtractionNdaDraftForm(extractionResult.nda);
      setExtractionNdaDraft(draftNda);
      setAcceptExtractionNda(!!draftNda && !dealHasNda(initialDeal as any));
      setExtractionHydrated(true);

      if (!embedded) {
        window.history.replaceState({}, document.title);
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractionResult, embedded, isEdit, existingDealReady]);

  // Create: pre-fill default-deal-config on load (Athene) and whenever Capital Source changes.
  // Edit: only when the user changes Capital Source — not on initial load from the API.
  useEffect(() => {
    if (extractionResult && !extractionHydrated) return;
    if (isEdit && !existingDealReady) return;

    const sourceId = deal.capital_source?.id ?? 1;

    if (isEdit) {
      if (prevEditCapitalSourceId.current === undefined) {
        prevEditCapitalSourceId.current =
          initialDeal?.capital_source?.id ?? 1;
        lastAppliedCapitalSourceId.current = prevEditCapitalSourceId.current;
        return;
      }
      if (deal.id !== initialDeal?.id) return;
      if (prevEditCapitalSourceId.current === sourceId) return;
      prevEditCapitalSourceId.current = sourceId;
    } else if (lastAppliedCapitalSourceId.current === sourceId) {
      return;
    }

    let cancelled = false;

    const loadDefaults = async () => {
      try {
        const response = await apiGet(
          `default-deal-config?capital_source_id=${sourceId}`,
        );
        if (cancelled || !response?.success || !response.data?.[0]) return;

        const defaults = dealConfigRowToPartialDeal(
          response.data[0] as Record<string, unknown>,
        );
        lastAppliedCapitalSourceId.current = sourceId;
        setDeal((prev) => ({
          ...prev,
          ...defaults,
          capital_source: prev.capital_source ?? emptyDeal.capital_source,
        }));
      } catch {
        // User can still fill the form manually
      }
    };

    loadDefaults();
    return () => {
      cancelled = true;
    };
  }, [
    deal.capital_source?.id,
    deal.id,
    extractionResult,
    extractionHydrated,
    isEdit,
    existingDealReady,
    initialDeal,
  ]);

  // Required list grows for Term Sheet Submitted and later closing statuses
  const requiredFields = getRequiredFields(deal);
  const hasValidationErrors = validationErrors.length > 0;
  const missingRequiredField = requiredFields.some(
    ({ field }) => !isDealFieldFilled(deal[field]),
  );
  // Fields commit on every keystroke, so keep the dirty check cheap: deal fields
  // are compared individually, and the related tables (which typing in a deal
  // field does not touch) are only re-compared when they change.
  const dealFieldsChanged = useMemo(
    () =>
      isEdit && originalDeal != null && hasDealFieldChanges(deal, originalDeal),
    [isEdit, deal, originalDeal],
  );
  const relatedRecordsChanged = useMemo(
    () =>
      isEdit &&
      hasRelatedRecordChanges({
        assets,
        originalAssets,
        tenants,
        originalTenants,
        contacts,
        originalContacts,
      }),
    [
      isEdit,
      assets,
      originalAssets,
      tenants,
      originalTenants,
      contacts,
      originalContacts,
    ],
  );
  const hasEditChanges =
    !isEdit ||
    (existingDealReady &&
      originalDeal != null &&
      (dealFieldsChanged || relatedRecordsChanged));
  const isSubmitDisabled =
    isSaving ||
    partiallySavedDealId != null ||
    missingRequiredField ||
    tenantAssetNameFieldHasError ||
    !canEdit ||
    (isEdit && !hasEditChanges);

  // Stable so memoized FormFields only re-render when their own value changes
  const updateDealField = useCallback((field: string, value: unknown) => {
    // Killing a deal asks for a kill reason first (KillReasonDialog below).
    if (
      field === "status" &&
      isKilledStatus(getStatusName(value)) &&
      !hasKillReason(dealRef.current)
    ) {
      setPendingKillStatus(value);
      return;
    }
    setDeal((prev) => ({ ...prev, [field]: value }));
    // Clear stale submit errors when the user edits the form
    setValidationErrors((prev) => (prev.length > 0 ? [] : prev));
  }, []);

  const dealOptionsMap = useMemo(
    () => ({
      sponsors,
      sources,
      countries,
      sectors,
      status: [...status].sort((a, b) => a.sort_order - b.sort_order),
      users,
      loanTypes,
      riskClasses,
      securityTypes,
      transactionTypes,
      currencies,
      killReasons,
      capitalSources,
    }),
    [
      sponsors,
      sources,
      countries,
      sectors,
      status,
      users,
      loanTypes,
      riskClasses,
      securityTypes,
      transactionTypes,
      currencies,
      killReasons,
      capitalSources,
    ],
  );

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;

    setIsSaving(true);
    setValidationErrors([]);

    try {
      const result = isEdit
        ? await saveUpdateDeal({
            deal,
            assets,
            tenants,
            contacts,
            originalAssets,
            originalTenants,
            originalContacts,
            assetIdsNeedingSave,
            referenceContacts: (referenceContacts || []) as any,
            updatedBy: user?.displayName,
            extractionId: extractionResult?.id,
            acceptExtractionNda,
            extractionNdaDraft,
          })
        : await saveCreateDeal({
            deal,
            assets,
            tenants,
            contacts,
            referenceContacts: (referenceContacts || []) as any,
            createdBy: user?.displayName,
            extractionId: extractionResult?.id,
            acceptExtractionNda,
            extractionNdaDraft,
          });

      if (!result.ok) {
        if (result.savedDealId != null) {
          if (isEdit) {
            clearAssetsNeedingSave();
          }
          setPartiallySavedDealId(result.savedDealId);
        }
        if (result.validationErrors) {
          setValidationErrors(result.validationErrors);
        }
        if (result.apiResponse) {
          showErrorFromResponse(result.apiResponse);
        } else if (result.message) {
          showError(undefined, result.message, result.title || "Error");
        }
        return;
      }

      if (isEdit) {
        clearAssetsNeedingSave();
      }
      navigate(`/deals/${result.dealId}`);
    } catch (error) {
      showError(
        undefined,
        error instanceof Error
          ? error.message
          : "An unexpected error occurred. Please try again.",
        "Submission Error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  const embeddedOnSubmit = useCallback(() => {
    void handleSubmitRef.current();
  }, []);

  const embeddedOnDelete = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const submitLabel = isEdit ? "Update Deal" : "Create Deal";

  useEffect(() => {
    if (!embedded || !onEmbeddedToolbarChange) return;
    onEmbeddedToolbarChange({
      submitLabel,
      isSubmitDisabled,
      isLoading: isSaving,
      showDelete: !!deleteOption,
      onSubmit: embeddedOnSubmit,
      onDelete: embeddedOnDelete,
    });
  }, [
    embedded,
    onEmbeddedToolbarChange,
    submitLabel,
    isSubmitDisabled,
    isSaving,
    deleteOption,
    embeddedOnSubmit,
    embeddedOnDelete,
  ]);

  useEffect(() => {
    if (!embedded || !onEmbeddedToolbarChange) return;
    return () => onEmbeddedToolbarChange(null);
  }, [embedded, onEmbeddedToolbarChange]);

  const handleErrorDialogClose = () => {
    hideError();
    if (partiallySavedDealId != null) {
      navigate(`/deals/${partiallySavedDealId}`);
    }
  };

  // Preview refresh: replace only the previous extraction rows so persisted
  // and manually added tenants are not dropped (and then deleted on save).
  const handleExtractionTenantsChange = (incoming: any[]) => {
    const previousIds = extractionTenantIds;
    setExtractionTenantIds(new Set(incoming.map((tenant) => tenant?.id)));
    setTenants(
      (prev) =>
        replaceExtractionTenants(prev as any[], previousIds, incoming) as any,
    );
  };

  const handleExtractionError = (message: string, title: string) => {
    showError(undefined, message, title);
  };

  const extractionReviewProps = liveExtraction
    ? {
        extraction: liveExtraction,
        deal,
        assets,
        columnOverrides,
        rowRange: tenantRowRange,
        onColumnOverridesChange: setColumnOverrides,
        onRowRangeChange: (field: "start" | "end", value: number) => {
          setTenantRowRange((prev) => ({ ...prev, [field]: value }));
        },
        onExtractionChange: setLiveExtraction,
        onTenantsChange: handleExtractionTenantsChange,
        ndaDraft: extractionNdaDraft,
        acceptNda: acceptExtractionNda,
        onAcceptNdaChange: setAcceptExtractionNda,
        onNdaDraftChange: (next: ExtractionNdaDraft) =>
          setExtractionNdaDraft(next),
        onError: handleExtractionError,
      }
    : null;

  return (
    <Box sx={{ position: "relative", zIndex: 1 }}>
      <ErrorDialog
        open={errorDialog.open}
        onClose={handleErrorDialogClose}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />

      <KillReasonDialog
        open={pendingKillStatus !== null}
        dealName={deal.deal_name || "This deal"}
        onCancel={() => setPendingKillStatus(null)}
        onConfirm={(reason: KillReason | null) => {
          const status = pendingKillStatus;
          setPendingKillStatus(null);
          if (reason) updateDealField("kill_reason", reason);
          setDeal((prev) => ({
            ...prev,
            status: status as PartialDeal["status"],
          }));
        }}
      />

      {editDealId != null && (
        <ConfirmDialog
          open={deleteDialogOpen}
          endpoint="deal"
          id={editDealId}
          title="Delete Deal"
          description="Are you sure you want to delete this deal? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onClose={() => setDeleteDialogOpen(false)}
          onSuccess={() => navigate("/")}
        />
      )}

      {!embedded && (
        <Box sx={{ position: "sticky", zIndex: 10, marginBottom: "10px" }}>
          <SimpleToolbar>
            {isEdit ? (
              <CustomButton
                handleButtonPress={() => navigate(`/deals/${editDealId}`)}
                isButtonDisabled={false}
                solid={false}
                text="Cancel"
                data-position="left"
              />
            ) : null}
            <CustomButton
              handleButtonPress={handleSubmit}
              isButtonDisabled={isSubmitDisabled}
              text={submitLabel}
              isLoading={isSaving}
              data-position="left"
            />
            {deleteOption ? (
              <CustomButton
                handleButtonPress={() => setDeleteDialogOpen(true)}
                isButtonDisabled={!canEdit}
                text="Delete Deal"
                isDeleteButton={true}
                data-position="right"
              />
            ) : null}
          </SimpleToolbar>
        </Box>
      )}

      {isSaving ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "40vh",
          }}
        >
          <CircularProgress size={80} />
        </Box>
      ) : (
        <>
          {/* Required fields change with Status (extra set for advanced statuses) */}
          <Box
            sx={{
              maxWidth: "95%",
              margin: "0 auto",
              paddingX: 4,
              paddingBottom: 2,
            }}
          >
            <Box
              sx={{
                padding: 2,
                backgroundColor: hasValidationErrors ? "#fff4e5" : "#f0f7ff",
                borderRadius: 1,
                border: hasValidationErrors
                  ? "1px solid #ff9800"
                  : "1px solid #2196f3",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  mb: 1,
                  color: hasValidationErrors ? "#e65100" : "#1565c0",
                }}
              >
                Required Fields:
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {requiredFields.map(({ field, label }) => {
                  const isFilled = isDealFieldFilled(deal[field]);
                  const hasError = validationErrors.some(
                    (e) => e.field === field,
                  );

                  return (
                    <Box
                      key={field}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        padding: "4px 12px",
                        borderRadius: "16px",
                        backgroundColor: hasError
                          ? "#ffcdd2"
                          : isFilled
                            ? "#c8e6c9"
                            : "#fff",
                        border: `1px solid ${
                          hasError ? "#f44336" : isFilled ? "#4caf50" : "#ccc"
                        }`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: hasError
                            ? "#f44336"
                            : isFilled
                              ? "#4caf50"
                              : "#999",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: hasError ? "#c62828" : "#333" }}
                      >
                        {label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              {hasValidationErrors && (
                <Box sx={{ mt: 1 }}>
                  {validationErrors.map((error, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      sx={{ display: "block", color: "#d32f2f" }}
                    >
                      • {error.message}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {extractionFileName && (
            <Box
              sx={{
                maxWidth: "95%",
                margin: "0 auto",
                paddingX: 4,
                paddingBottom: 2,
              }}
            >
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Data loaded from extraction:</strong>{" "}
                  {extractionFileName}
                </Typography>
              </Alert>
            </Box>
          )}

          {/* AI extraction + Excel template upload (create-deal UX) */}
          {!embedded && (
            <Box
              sx={{
                maxWidth: "95%",
                margin: "0 auto",
                paddingX: 4,
                paddingBottom: 2,
                display: "flex",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ flex: "1 1 400px", minWidth: "400px" }}>
                <DealAiExtraction dealId={editDealId} />
              </Box>
              <Box sx={{ flex: "1 1 400px", minWidth: "400px" }}>
                <DealExcelUpload dealId={editDealId} />
              </Box>
            </Box>
          )}

          <Box sx={{ maxWidth: "95%", margin: "0 auto", padding: 4 }}>
            <DynamicForm
              config={dealFieldConfig}
              values={deal}
              requiredFields={requiredFields.map(({ field }) => field)}
              onChange={updateDealField}
              optionsMap={dealOptionsMap}
            />

            {extractionReviewProps && (
              <DealExtractionReview
                section="nda"
                {...extractionReviewProps}
              />
            )}

            {/* Commentary */}
            <Box sx={{ paddingTop: 3, paddingBottom: 1 }}>
              {renderSubTitle("Commentary")}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", gap: "10px" }}>
              <Box
                sx={{ display: "flex", flexDirection: "column", width: "50%" }}
              >
                <Box sx={{ mt: "10px" }}>
                  {renderMultiLineTextInput(
                    "Deal Purpose",
                    deal.deal_purpose || "",
                    (val) => updateDealField("deal_purpose", val),
                  )}
                </Box>
                <Box sx={{ mt: "10px" }}>
                  {renderMultiLineTextInput(
                    "Deal Summary",
                    deal.deal_summary || "",
                    (val) => updateDealField("deal_summary", val),
                  )}
                </Box>
                <Box sx={{ mt: "10px" }}>
                  {renderMultiLineTextInput("Risks", deal.risks || "", (val) =>
                    updateDealField("risks", val),
                  )}
                </Box>
              </Box>
              <Box
                sx={{ display: "flex", flexDirection: "column", width: "50%" }}
              >
                <Box sx={{ mt: "10px" }}>
                  {renderMultiLineTextInput(
                    "Strategy",
                    deal.strategy || "",
                    (val) => updateDealField("strategy", val),
                  )}
                </Box>
                <Box sx={{ mt: "10px" }}>
                  {renderMultiLineTextInput(
                    "Market Summary",
                    deal.market_summary || "",
                    (val) => updateDealField("market_summary", val),
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ maxWidth: "95%", margin: "0 auto", padding: 4, pt: 0 }}>
            <ExtractedContactsTable
              dealId={editDealId}
              allContacts={referenceContacts as any}
            />
          </Box>

          {extractionReviewProps && (
            <Box sx={{ maxWidth: "95%", margin: "0 auto", padding: 4, pt: 0 }}>
              <DealExtractionReview
                section="columns"
                {...extractionReviewProps}
              />
            </Box>
          )}

          <Box sx={{ maxWidth: "95%", margin: "0 auto", padding: 4, pt: 0 }}>
            <ExtractedAssetsTable dealId={editDealId} />
          </Box>

          <Box sx={{ maxWidth: "95%", margin: "0 auto", padding: 4, pt: 0 }}>
            <ExtractedTenantsTable
              assetId={null}
              editMode={true}
              syncTenantAssetNameErrorToContext
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default CreateDealUpdate;
